import { useCallback, useEffect, useRef } from 'react';

import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import {
	createNativeEmbedChildClient,
	maybeParseNativeEmbedParams,
	NativeEmbedCoreFeature,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/child-client';

import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

/**
 * Initialises the native embed child client when a page is running inside a
 * native embed iframe (i.e. the URL contains `nativeEmbedChildId` and
 * `nativeEmbedParentId` query params set by the host). Gated behind the
 * `cc-maui-experiment` experiment.
 *
 * Should be called from embedded view pages, embedded edit pages, and
 * embedded live docs.
 *
 * @returns sendChildCommand — a stable callback to send a child command to the
 * native embed host, or a no-op if not running inside a native embed iframe.
 */
export const usePageNativeEmbedClient = (): {
	sendChildCommand: (command: NativeEmbedChildCommand) => void;
	triggerToggleFullScreen: () => void;
} => {
	const clientRef = useRef<ReturnType<typeof createNativeEmbedChildClient> | null>(null);

	useEffect(() => {
		if (
			!UNSAFE_expValNoExposure('cc-maui-experiment', 'isEnabled', false) &&
			!fg('platform_native_embeds_rollout_non_maui_experience')
		) {
			return;
		}

		const params = maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));
		if (!params) {
			return;
		}

		const bridge = getBrowserBridgeAPI();
		const { childClientId, parentClientId } = params;

		const supportedFeatures = new Set<NativeEmbedFeature>([NativeEmbedCoreFeature.NotifyHostClick]);

		const client = createNativeEmbedChildClient(
			bridge,
			childClientId,
			parentClientId,
			supportedFeatures,
		);

		clientRef.current = client;

		return () => {
			client.destroy();
			clientRef.current = null;
		};
	}, []);

	const sendChildCommand = useCallback((command: NativeEmbedChildCommand) => {
		clientRef.current?.sendChildCommand(command);
	}, []);

	const triggerToggleFullScreen = useCallback(() => {
		clientRef.current?.sendToggleFullScreen();
	}, []);

	return { sendChildCommand, triggerToggleFullScreen };
};
